import React from "react";
import { AlertCircle, Check, ArrowRight, CalendarDays } from "lucide-react";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";
import { Field, ErrorBox, INPUT } from "@/components/booking/parts";

export const Step1DatesGuests = ({ ctx }) => {
  const {
    t, v, TODAY, form, update, calRange, onRangeChange, calBlockedDays, availLoading,
    setGuestsCount, labels, setGuestType, setGuestAge, setGuestCot,
    effGuests, maxRooms, roomsWanted, setRoomsWanted,
    datesValid, staysFeasible, fallbackCounts, liveAlternatives, applyDates, fmtRange,
    state, goVerify,
  } = ctx;
  const { GUEST_LABEL, ADULT, CHILD, AGE_LABEL, YEARS, UNDER1, COT_LABEL } = labels;

  return (
    <div className="space-y-6" data-testid="step-dates-guests">
      <div>
        <h2 className="font-display text-2xl md:text-3xl text-[#1A1A1A]">{v("step1Title")}</h2>
        <p className="mt-2 text-sm text-[#5C5C5C]">{v("step1Sub")}</p>
      </div>

      <AvailabilityCalendar range={calRange} onRangeChange={onRangeChange} blockedDays={calBlockedDays} loading={availLoading} />

      <div className="grid md:grid-cols-2 gap-5">
        <Field label={t("booking.labels.arrival")} required>
          <input data-testid="input-arrival" type="date" required min={TODAY} value={form.arrival} onChange={update("arrival")} className={INPUT} />
        </Field>
        <Field label={t("booking.labels.departure")} required>
          <input data-testid="input-departure" type="date" required min={form.arrival || TODAY} value={form.departure} onChange={update("departure")} className={INPUT} />
        </Field>
      </div>

      <Field label={t("booking.labels.guests")} required>
        <select data-testid="input-guests" required value={form.guests} onChange={setGuestsCount} className={INPUT + " md:max-w-xs"}>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </Field>

      <div>
        <div className="text-sm font-medium text-[#1A1A1A] mb-2">{t("booking.labels.composition")}<span className="text-[#7A2B33]">*</span></div>
        <div className="space-y-3">
          {(form.guest_types || []).map((gt, i) => {
            const age = (form.guest_ages || [])[i];
            const isInfant = gt === "child" && age !== "" && Number(age) < 3;
            return (
              <div key={i} className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-[#F2EFEB]/60">
                <span className="text-xs text-[#5C5C5C] w-16">{GUEST_LABEL} {i + 1}</span>
                <select data-testid={`guest-type-${i}`} value={gt} onChange={(e) => setGuestType(i, e.target.value)} className={INPUT + " w-auto flex-1 min-w-[120px] py-2"}>
                  <option value="adult">{ADULT}</option>
                  <option value="child">{CHILD}</option>
                </select>
                {gt === "child" && (
                  <select data-testid={`guest-age-${i}`} value={age} onChange={(e) => setGuestAge(i, e.target.value)} className={INPUT + " w-auto py-2"}>
                    <option value="">{AGE_LABEL}</option>
                    {Array.from({ length: 18 }, (_, a) => (<option key={a} value={a}>{a === 0 ? UNDER1 : `${a} ${YEARS}`}</option>))}
                  </select>
                )}
                {isInfant && (
                  <label className="flex items-center gap-2 text-xs text-[#1A1A1A] cursor-pointer">
                    <input type="checkbox" data-testid={`guest-cot-${i}`} checked={!!(form.guest_cots || [])[i]} onChange={(e) => setGuestCot(i, e.target.checked)} className="w-4 h-4 accent-[#1D3557]" />
                    {COT_LABEL}
                  </label>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-[#5C5C5C]">{v("guestsHint")}</p>
      </div>

      {effGuests >= 2 && maxRooms >= 2 && (
        <Field label={v("roomsWanted")}>
          <select data-testid="input-rooms-wanted" value={roomsWanted} onChange={(e) => setRoomsWanted(Number(e.target.value))} className={INPUT + " md:max-w-xs"}>
            {Array.from({ length: maxRooms }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n === 1 ? v("oneRoom") : v("nRooms").replace("{n}", n)}</option>
            ))}
          </select>
          <p className="mt-2 text-xs text-[#5C5C5C]">{v("roomsWantedHint")}</p>
        </Field>
      )}

      {datesValid && staysFeasible === true && (
        <div data-testid="live-available" className="flex items-center gap-2 text-sm text-[#2A4B3C] bg-[#2A4B3C]/8 rounded-xl px-4 py-3">
          <Check className="w-4 h-4 flex-shrink-0" /> {v("liveAvailable").replace("{n}", effGuests)}
        </div>
      )}

      {datesValid && staysFeasible === false && (
        <div data-testid="live-unavailable" className="rounded-xl bg-[#7A2B33]/8 border border-[#7A2B33]/20 p-4 space-y-3">
          <div className="flex items-start gap-2 text-sm text-[#7A2B33] font-medium">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {v("liveUnavailable").replace("{n}", effGuests).replace("{r}", roomsWanted === 1 ? v("oneRoom") : v("nRooms").replace("{n}", roomsWanted))}
          </div>
          {fallbackCounts.length > 0 && (
            <div data-testid="live-fallback-rooms">
              <p className="text-xs text-[#5C5C5C] mb-2">{v("fallbackTitle")}</p>
              <div className="flex flex-wrap gap-2">
                {fallbackCounts.map((rc) => (
                  <button key={rc} data-testid={`fallback-rooms-${rc}`} onClick={() => setRoomsWanted(rc)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#2A4B3C]/10 border border-[#2A4B3C]/25 text-sm text-[#2A4B3C] hover:bg-[#2A4B3C] hover:text-white transition-all">
                    <Check className="w-3.5 h-3.5" /> {rc === 1 ? v("oneRoom") : v("nRooms").replace("{n}", rc)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {liveAlternatives.length > 0 && (
            <div data-testid="live-alternatives">
              <p className="text-xs text-[#5C5C5C] mb-2">{v("liveAltTitle")}</p>
              <div className="flex flex-wrap gap-2">
                {liveAlternatives.map((alt, ai) => (
                  <button key={ai} data-testid={`live-alt-${ai}`} onClick={() => applyDates(alt.arrival, alt.departure)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#1D3557]/25 text-sm text-[#1D3557] hover:bg-[#1D3557] hover:text-white transition-all">
                    <CalendarDays className="w-3.5 h-3.5" /> {fmtRange(alt.arrival, alt.departure)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {state.error && <ErrorBox msg={state.error} />}

      <button data-testid="verify-availability-btn" onClick={goVerify} disabled={!datesValid}
        className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#1D3557] text-white font-medium hover:bg-[#345B73] transition-all disabled:opacity-50">
        {v("verifyBtn")} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};
